import { v4 as uuidv4 } from 'uuid';

const decodeHTMLEntities = (text) => {
  const entities = {
    amp: '&',
    apos: "'",
    '#x27': "'",
    '#x2F': '/',
    '#39': "'",
    '#47': '/',
    lt: '<',
    gt: '>',
    nbsp: ' ',
    quot: '"',
  };
  return text.replace(/&([^;]+);/gm, function (match, entity) {
    return entities[entity] || match;
  });
};

export const formatResponse = (htmlReponse, isEnd) => {
  let formatted = '';
  if (htmlReponse) {
    formatted = htmlReponse.replaceAll('```html', '');
    formatted = formatted.startsWith('<pre>') ? formatted.substring(5) : formatted;
    formatted = formatted.endsWith('</pre>')
      ? formatted.substring(0, formatted.length - 6)
      : formatted;
    formatted = formatted.startsWith('<code>') ? formatted.substring(6) : formatted;
    formatted = formatted.endsWith('</code>')
      ? formatted.substring(0, formatted.length - 7)
      : formatted;
    formatted = formatted.startsWith('"') ? formatted.substring(1) : formatted;
    formatted = formatted.endsWith('"') ? formatted.substring(0, formatted.length - 1) : formatted;
    formatted = formatted.replaceAll('\\n', '');
    formatted = decodeHTMLEntities(formatted);
    formatted = formatted.startsWith('<title>') ? formatted.substring(7) : formatted;
    formatted = formatted.endsWith('</title>')
      ? formatted.substring(0, formatted.length - 8)
      : formatted;
    // commenting this code to check later
    // const preIndex = formatted.indexOf("<pre>");
    // if (preIndex !== -1) {
    //     const closingTagIndex = formatted.indexOf("</pre>", preIndex);
    //     if (closingTagIndex !== -1) {
    //         formatted = formatted.substring(0, preIndex) + formatted.substring(closingTagIndex + 6);
    //     }
    // }
    if (isEnd) {
      formatted = formatted.replaceAll('```', '');
      formatted = formatted.replaceAll('"(\\r\\n|\\n|\\r)/g)}', '');
      formatted = formatted.endsWith('}')
        ? formatted.substring(0, formatted.length - 1)
        : formatted;
      formatted = formatted.endsWith('"')
        ? formatted.substring(0, formatted.length - 1)
        : formatted;
    }
    formatted = formatted.endsWith('"') ? formatted.substring(0, formatted.length - 1) : formatted;
  }
  // console.log('formatted', formatted);
  return formatted;
};

// We're adding the phrase "Your response should be formatted using HTML tags." along with an additional "." to the user's query in the streaming API.
// Consequently, it's stored as is in the history. We must exclude this when displaying the active chat history
// TODO: We can remove this logic once this phrase is sent as part of the instructions insted of user query
// The phrase is now removed from the input query, but to support older chats and archived chats which might have
// this phrase, we are keeping this code.
export const formatInput = (inputText: string) => {
  inputText = inputText.replace(/Your response must be formatted using HTML tags./i, '').trim();
  return inputText;
};

export const formatMarkdown = (markdownResponse) => {
  return markdownResponse.replace(/\\n/gi, '\n ');
};

export const generateChatSessionId = () => {
  return uuidv4();
};

export const getNotificationMessage = (
  chatAgent,
  availableImageQuota,
  name,
  isAppFileAttached = false
) => {
  if (chatAgent === 'Document' || isAppFileAttached) {
    return 'This is a read only chat because one or more documents from this chat has been deleted';
  }

  if (
    chatAgent === 'app-agent' &&
    availableImageQuota !== undefined &&
    availableImageQuota <= 0 &&
    name === 'Image Generator'
  ) {
    return "You've reached your 50 image limit. The count will reset on the 1st of each month.";
  }

  return 'This is a read only chat because the AI agent associated with this chat is no longer available';
};

function uint8ArrayToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function encryptAesWithSha256(plaintext, password) {
  const encoder = new TextEncoder();
  const plainBytes = encoder.encode(plaintext);
  const passwordBytes = encoder.encode(password);

  const keyMaterial = await crypto.subtle.digest('SHA-256', passwordBytes);
  const key = await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt']
  );

  const iv = crypto.getRandomValues(new Uint8Array(16));

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-CBC', iv: iv }, key, plainBytes);

  const encryptedBytes = new Uint8Array(iv.length + ciphertext.byteLength);
  encryptedBytes.set(iv, 0);
  encryptedBytes.set(new Uint8Array(ciphertext), iv.length);

  return uint8ArrayToBase64(encryptedBytes);
}

const removeCitationMarkers = (markdown: string): string => {
  return markdown.replace(/\[\s*\d+\s*\*\s*\]/g, '');
};

export const fixMarkdownTable = (markdown: string): string => {
  if (!markdown) return markdown;

  const cleanedMarkdown = removeCitationMarkers(markdown);
  const lines = cleanedMarkdown.split('\n');

  return lines
    .map((line, index) => {
      const isSeparatorRow = /^\|[\s|:-]+\|$/.test(line.trim());
      if (!isSeparatorRow) return line;

      const headerLine = lines[index - 1];
      if (!headerLine) return line;

      const headerCols = headerLine.trim().split('|').filter(Boolean).length;
      const separatorParts = line.trim().split('|').filter(Boolean);
      const separatorCols = separatorParts.length;

      if (headerCols === separatorCols) return line;

      if (headerCols > separatorCols) {
        const missing = headerCols - separatorCols;
        const trimmed = line.trimEnd();
        const padding = Array(missing).fill('---|').join('');
        return trimmed.endsWith('|')
          ? trimmed.slice(0, -1) + '|' + padding
          : trimmed + '|' + padding;
      }

      if (headerCols < separatorCols) {
        const trimmed = separatorParts.slice(0, headerCols);
        return '|' + trimmed.join('|') + '|';
      }

      return line;
    })
    .join('\n');
};

// base 64 text encoder
export const encodeBase64 = (str: string) => {
  if (!str) return '';

  const bytes = new TextEncoder().encode(str);
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
  return btoa(binString);
};

// last sync time function
export const getLastSyncedText = (isoTimestamp) => {
  if (!isoTimestamp) return 'Not synced yet';

  const synced = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - synced.getTime();
  if (diffMs < 0) return 'Just now';

  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  // calendar-accurate month count instead of days/30
  let months =
    (now.getUTCFullYear() - synced.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - synced.getUTCMonth());
  if (now.getUTCDate() < synced.getUTCDate()) months -= 1;

  return `${months} month${months === 1 ? '' : 's'} ago`;
};
